import type { GroupSubscriptionData } from '@shared/dream-components';

type OverlapPlan = { groupManagedBy?: string | null; isFree: boolean; tierName?: string };
type OverlapGroup = { status: GroupSubscriptionData['status']; tier_name: string };

/**
 * Group access arrives two ways: occupying someone else's seat (a tier with
 * `groupManagedBy`, J18) OR purchasing a group yourself (J33 — surfaces only in
 * the `group_subscriptions` loader, never as a `groupManagedBy` tier).
 *
 * The banner only fires when the group access duplicates a paid individual plan
 * on the *same tier* — that's the genuine double-pay. Different tiers (e.g. a
 * paused individual Premium plus an All Access group seat) are distinct
 * products, not an overlap, so they must not trip it (BEE-20105).
 *
 * Only `active` and `past_due` groups grant access. A pre-active group such as payment_pending, awaiting_payment,
 * or pre_approved has no seats yet, and a dead group such as payment_failed or canceled has none either, so neither
 * can overlap a paid individual plan and neither trips the banner.
 */
export const hasGroupAndIndividualOverlap = ({
  groupSubscriptionsEnabled,
  currentPlans,
  groupSubscriptions,
}: {
  groupSubscriptionsEnabled: boolean;
  currentPlans: OverlapPlan[];
  groupSubscriptions: OverlapGroup[];
}): boolean => {
  if (!groupSubscriptionsEnabled) return false;

  const paidIndividualTierNames = new Set(
    currentPlans.filter((plan) => !plan.groupManagedBy && !plan.isFree && plan.tierName).map((plan) => plan.tierName),
  );
  if (paidIndividualTierNames.size === 0) return false;

  const isGroupOccupantOnSameTier = currentPlans.some(
    (plan) => Boolean(plan.groupManagedBy) && paidIndividualTierNames.has(plan.tierName),
  );
  const isActiveGroupAdminOnSameTier = groupSubscriptions.some(
    (group) =>
      (group.status === 'active' || group.status === 'past_due') && paidIndividualTierNames.has(group.tier_name),
  );

  return isGroupOccupantOnSameTier || isActiveGroupAdminOnSameTier;
};
